// The logo engine: a design (see DEFAULT_CONFIG) becomes a finished, self-contained SVG. All text is turned into outlines with
// the font files, so a logo looks the same everywhere (browsers, design tools, printers) and no font has to be installed.
import { getFont } from "./fonts";
import { ICONS, CONTAINERS, iconMarkup } from "./icons";
import { contrast, darken, lighten, mix, normaliseHex, readableOn } from "./color";

export const LAYOUTS = {
  stacked: "Icon above name",
  horizontal: "Icon left of name",
  horizontalRight: "Icon right of name",
  wordmark: "Name only",
  emblem: "Round emblem",
  iconOnly: "Icon only",
};
export const EFFECTS = { none: "Flat", shadow: "Soft shadow", "3d": "3D depth", gloss: "Glossy", inline: "Inner line" };
export const NAME_TONES = { dark: "Dark", primary: "Primary colour", secondary: "Secondary colour", accent: "Accent colour", twoTone: "Two colours", gradient: "Gradient" };
export const DECORS = { none: "None", rule: "Line between", flank: "Lines beside tagline", bar: "Small bar" };

export const DEFAULT_CONFIG = {
  name: "Your Brand",
  tagline: "",
  industry: "general",
  mood: "modern",
  layout: "stacked",
  source: "icon", // icon | letters | none
  icon: "hex",
  letters: "",
  container: "circle",
  containerStyle: "solid", // solid | gradient | outline
  font: "montserrat",
  textFont: "inter",
  nameCase: "upper",
  taglineCase: "upper",
  tracking: 60,
  taglineTracking: 280,
  nameTone: "dark",
  nameLines: "auto", // auto | 1 | 2
  iconScale: 1,
  nameScale: 1,
  taglineScale: 1,
  justify: true,
  effect: "none",
  decor: "none",
  colors: { primary: "#0b5ed7", secondary: "#00b4d8", accent: "#0a2540" },
};

const r2 = (n) => Math.round(n * 100) / 100;
const SVG_NS = "http://www.w3.org/2000/svg";

export const applyCase = (text, mode) => {
  const t = String(text || "").trim();
  if (mode === "upper") return t.toUpperCase();
  if (mode === "lower") return t.toLowerCase();
  if (mode === "title") return t.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  return t;
};

export class FontNotReady extends Error {}

// An outline as SVG path data. (opentype.js's own toPathData() produces NaN at some exact font sizes, so this does it.)
const num = (v) => String(Math.round(v * 100) / 100);
function commandsToD(commands) {
  return commands.map((c) => {
    if (c.type === "M" || c.type === "L") return `${c.type}${num(c.x)} ${num(c.y)}`;
    if (c.type === "Q") return `Q${num(c.x1)} ${num(c.y1)} ${num(c.x)} ${num(c.y)}`;
    if (c.type === "C") return `C${num(c.x1)} ${num(c.y1)} ${num(c.x2)} ${num(c.y2)} ${num(c.x)} ${num(c.y)}`;
    return "Z";
  }).join("");
}

// ------------------------------------------------------------------------------------------ text as outlines
// One line of text as outlines: [{ d, x, adv, word }] per glyph plus the line's measurements.
function layoutLine(fontKey, text, size, trackingPx = 0) {
  const font = getFont(fontKey);
  if (!font) throw new FontNotReady(fontKey);
  const scale = size / font.unitsPerEm;
  const chars = Array.from(text);
  const glyphs = chars.map((ch) => font.charToGlyph(ch)); // one glyph per character: no ligature shaping, which some fonts' tables break
  let x = 0;
  let word = 0;
  const out = [];
  const missing = [];
  glyphs.forEach((glyph, i) => {
    const ch = chars[i] || "";
    if (/\s/.test(ch)) word += 1;
    else if (glyph.index === 0 && ch) missing.push(ch);
    const path = glyph.getPath(0, 0, size);
    const box = path.commands.length ? path.getBoundingBox() : null;
    out.push({ d: path.commands.length ? commandsToD(path.commands) : "", x, adv: glyph.advanceWidth * scale + trackingPx, word, box, ch });
    const next = glyphs[i + 1];
    let kern = 0;
    try { kern = next ? font.getKerningValue(glyph, next) * scale : 0; } catch (e) { kern = 0; }
    x += glyph.advanceWidth * scale + trackingPx + kern;
  });
  const drawn = out.filter((g) => g.box);
  const left = drawn.length ? Math.min(...drawn.map((g) => g.x + g.box.x1)) : 0;
  const right = drawn.length ? Math.max(...drawn.map((g) => g.x + g.box.x2)) : 0;
  const top = drawn.length ? Math.min(...drawn.map((g) => g.box.y1)) : 0;
  const bottom = drawn.length ? Math.max(...drawn.map((g) => g.box.y2)) : 0;
  const hBox = font.charToGlyph("H").getPath(0, 0, size).getBoundingBox();
  return { glyphs: out, left, right, width: right - left, top, bottom, cap: hBox.y2 - hBox.y1, missing, count: out.length, trackingPx };
}

// Tracking is given in thousandths of an em.
const trackPx = (size, tracking) => (size * tracking) / 1000;

function lineMarkup(line, x, baseline, fills, lineWidth) {
  // fills: one colour for the whole line, or [first-word colour, other-words colour]
  const groups = {};
  line.glyphs.forEach((g) => {
    if (!g.d) return;
    const key = Array.isArray(fills) ? (g.word === 0 ? 0 : 1) : 0;
    (groups[key] = groups[key] || []).push(`<path d="${g.d}" transform="translate(${r2(g.x)} 0)"/>`);
  });
  const dx = x - line.left;
  return Object.entries(groups).map(([key, paths]) => `<g fill="${Array.isArray(fills) ? fills[key] : fills}" transform="translate(${r2(dx)} ${r2(baseline)})">${paths.join("")}</g>`).join("");
}

// Splits a name into two balanced lines at a space.
function splitName(text) {
  const words = text.split(/\s+/);
  if (words.length < 2) return [text];
  let best = 1;
  let bestGap = Infinity;
  for (let i = 1; i < words.length; i += 1) {
    const gap = Math.abs(words.slice(0, i).join(" ").length - words.slice(i).join(" ").length);
    if (gap < bestGap) { best = i; bestGap = gap; }
  }
  return [words.slice(0, best).join(" "), words.slice(best).join(" ")];
}

export function initialsOf(config) {
  const custom = (config.letters || "").trim();
  if (custom) return custom.slice(0, 3).toUpperCase();
  const words = String(config.name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "A";
  return (words.length === 1 ? words[0].slice(0, 1) : words.slice(0, 2).map((w) => w.charAt(0)).join("")).toUpperCase();
}

// ------------------------------------------------------------------------------------------ colours
export function paletteFor(config, mode = "color") {
  const primary = normaliseHex(config.colors.primary, "#0b5ed7");
  const secondary = normaliseHex(config.colors.secondary, primary);
  const accent = normaliseHex(config.colors.accent, darken(primary, 0.5));
  const tone = config.nameTone;
  const inkOf = (base) => mix(base, "#0b0f1a", 0.82);
  const pal = { primary, secondary, accent, fill2: secondary, outline: false };
  if (mode === "black") {
    Object.assign(pal, { fill: "#111111", fill2: "#111111", glyph: "#ffffff", glyphAccent: "#ffffff", text: "#111111", tagline: "#333333", rule: "#111111", iconMain: "#111111", iconAccent: "#111111", mono: true });
    return pal;
  }
  if (mode === "white") {
    Object.assign(pal, { fill: "#ffffff", fill2: "#ffffff", glyph: "#111111", glyphAccent: "#111111", text: "#ffffff", tagline: "#e5e7eb", rule: "#ffffff", iconMain: "#ffffff", iconAccent: "#ffffff", mono: true });
    return pal;
  }
  let fill = primary;
  let fill2 = secondary;
  let text;
  let tagline;
  if (mode === "light") { // for dark backgrounds
    const ground = "#0f172a";
    if (contrast(fill, ground) < 3) fill = lighten(fill, 0.5);
    if (contrast(fill2, ground) < 3) fill2 = lighten(fill2, 0.5);
    text = "#ffffff";
    tagline = "#cbd5e1";
  } else {
    text = tone === "primary" ? primary : tone === "secondary" ? secondary : tone === "accent" ? accent : inkOf(primary);
    tagline = mix(tone === "dark" || tone === "twoTone" || tone === "gradient" ? inkOf(primary) : text, "#ffffff", 0.38);
  }
  const outline = config.containerStyle === "outline";
  const base = outline ? "#ffffff" : fill;
  const glyph = outline ? fill : readableOn(base);
  let glyphAccent = outline ? fill2 : (contrast(accent, base) >= 2.4 ? accent : (contrast(fill2, base) >= 2.4 && fill2 !== fill ? fill2 : glyph));
  if (mode === "light" && !outline && contrast(accent, base) < 2.4) glyphAccent = glyph;
  const hasContainer = config.source !== "none" && config.container !== "none";
  const iconMain = hasContainer ? glyph : fill;
  const iconAccent = hasContainer ? glyphAccent : (fill2 !== fill ? fill2 : (contrast(accent, mode === "light" ? "#0f172a" : "#ffffff") >= 2.4 ? accent : lighten(fill, 0.45)));
  return { ...pal, fill, fill2, glyph, glyphAccent, text, tagline, rule: fill2 !== fill ? fill2 : fill, iconMain, iconAccent, outline, tone, mono: false };
}

// ------------------------------------------------------------------------------------------ the mark
function markMarkup(config, pal, size, uid, parts) {
  // returns the markup of the symbol drawn in a size x size box (origin top-left)
  const k = size / 100;
  const container = config.container === "none" ? null : CONTAINERS[config.container];
  const hasContainer = !!container && config.source !== "none";
  let out = "";
  const gradId = `${uid}-mk`;
  const fillRef = config.containerStyle === "gradient" && !pal.mono ? `url(#${gradId})` : pal.fill;
  if (config.containerStyle === "gradient" && !pal.mono) {
    parts.defs.push(`<linearGradient id="${gradId}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${pal.fill}"/><stop offset="1" stop-color="${pal.fill2}"/></linearGradient>`);
  }
  let glyphBox = { x: 0, y: 0, s: 1 };
  if (hasContainer) {
    const shape = (extra) => `<path d="${container.d}" ${extra}/>`;
    if (config.effect === "3d") {
      const depth = Math.max(4, Math.round(size / 16));
      const side = darken(pal.mono ? pal.fill : pal.fill, 0.38);
      for (let i = depth; i >= 1; i -= 1) out += `<g transform="translate(0 ${r2((i * 1.15) / k)})">${shape(`fill="${side}"`)}</g>`;
    }
    if (config.containerStyle === "outline") out += shape(`fill="none" stroke="${pal.fill}" stroke-width="${config.effect === "inline" ? 6 : 7}" stroke-linejoin="round"`);
    else out += shape(`fill="${fillRef}"`);
    if (config.effect === "gloss" && !pal.mono && config.containerStyle !== "outline") {
      parts.defs.push(`<linearGradient id="${uid}-gl" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".62"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></linearGradient><clipPath id="${uid}-gc"><path d="${container.d}"/></clipPath>`);
      out += `<g clip-path="url(#${uid}-gc)"><ellipse cx="50" cy="-6" rx="78" ry="58" fill="url(#${uid}-gl)"/></g>`;
    }
    if (config.effect === "inline" && !pal.mono) {
      out += `<g transform="translate(50 50) scale(.86) translate(-50 -50)"><path d="${container.d}" fill="none" stroke="${config.containerStyle === "outline" ? pal.fill : pal.glyph}" stroke-opacity=".55" stroke-width="2.2" stroke-linejoin="round"/></g>`;
    }
    glyphBox = { s: container.inner, y: container.shift || 0 };
  } else {
    glyphBox = { s: 0.94, y: 0 };
  }
  let glyph = "";
  const scale = glyphBox.s;
  if (config.source === "letters") {
    const text = initialsOf(config);
    const size100 = 60;
    let line = layoutLine(config.font, text, size100, trackPx(size100, 0));
    const fit = Math.min(1, (92 * scale) / Math.max(line.width, 1), (74 * scale) / Math.max(line.cap, 1) * 1.1);
    const fontSize = size100 * fit;
    line = layoutLine(config.font, text, fontSize, 0);
    const cx = 50;
    const cy = 50 + glyphBox.y;
    glyph = `<g fill="${hasContainer ? pal.glyph : pal.iconMain}" transform="translate(${r2(cx - line.left - line.width / 2)} ${r2(cy + line.cap / 2)})">${line.glyphs.filter((g) => g.d).map((g) => `<path d="${g.d}" transform="translate(${r2(g.x)} 0)"/>`).join("")}</g>`;
    parts.missing.push(...line.missing);
  } else if (config.source === "icon" && ICONS[config.icon]) {
    const colors = { main: hasContainer ? pal.glyph : pal.iconMain, accent: hasContainer ? pal.glyphAccent : pal.iconAccent };
    const markup = iconMarkup(config.icon, colors, uid);
    glyph = `<g transform="translate(50 ${50 + glyphBox.y}) scale(${r2(scale)}) translate(-50 -50)">${markup}</g>`;
  }
  return `<g transform="scale(${r2(k)})">${out}${glyph}</g>`;
}

// ------------------------------------------------------------------------------------------ composing
export function buildLogo(config, options = {}) {
  const { mode = "color", uid = "lg", background = null, pad = 0.1 } = options;
  const cfg = { ...DEFAULT_CONFIG, ...config, colors: { ...DEFAULT_CONFIG.colors, ...(config.colors || {}) } };
  const pal = paletteFor(cfg, mode);
  const parts = { defs: [], body: "", missing: [] };
  const name = applyCase(cfg.name, cfg.nameCase) || "Your Brand";
  const tagText = applyCase(cfg.tagline, cfg.taglineCase);
  const nameFont = cfg.font;
  const tagFont = cfg.textFont;
  const layout = cfg.layout;
  const N = 60 * cfg.nameScale;
  const hasMark = cfg.source !== "none" && layout !== "wordmark";
  let box;

  // ---- text fills
  let nameFill = pal.text;
  if (!pal.mono && cfg.nameTone === "twoTone" && mode !== "light") nameFill = [mix(pal.primary, "#0b0f1a", 0.15), pal.secondary !== pal.primary ? pal.secondary : pal.accent];
  else if (!pal.mono && cfg.nameTone === "twoTone") nameFill = ["#ffffff", lighten(pal.fill2 !== pal.fill ? pal.fill2 : pal.fill, 0.2)];
  let gradientText = false;
  if (!pal.mono && cfg.nameTone === "gradient") {
    gradientText = true;
    nameFill = `url(#${uid}-gt)`;
  }

  if (layout === "emblem") {
    box = emblem(cfg, pal, uid, parts, name, tagText, nameFill);
  } else {
    // name lines
    let nameSize = N;
    const nameTracking = trackPx(nameSize, cfg.tracking);
    let lines = [name];
    const wantTwo = cfg.nameLines === "2" || (cfg.nameLines === "auto" && name.length > 18 && /\s/.test(name) && (layout === "stacked" || layout === "wordmark"));
    if (wantTwo) lines = splitName(name);
    const laid = lines.map((t) => layoutLine(nameFont, t, nameSize, nameTracking));
    parts.missing.push(...laid.flatMap((l) => l.missing));
    const cap = laid[0].cap;
    const lineGap = cap * 0.42;
    const nameW = Math.max(...laid.map((l) => l.width));
    // tagline
    let tagLine = null;
    let tagSize = N * 0.3 * cfg.taglineScale;
    if (tagText) {
      let tracking = trackPx(tagSize, cfg.taglineTracking);
      tagLine = layoutLine(tagFont, tagText, tagSize, tracking);
      const stackedish = layout === "stacked" || layout === "wordmark";
      const markW = hasMark ? (layout === "stacked" ? N * 2.5 * cfg.iconScale : 0) : 0;
      const limit = Math.max(nameW, markW, 240) * 1.05;
      if (tagLine.width > limit) { // too long: smaller
        tagSize *= limit / tagLine.width;
        tracking = trackPx(tagSize, cfg.taglineTracking);
        tagLine = layoutLine(tagFont, tagText, tagSize, tracking);
      } else if (cfg.justify && stackedish && tagLine.count > 2 && tagLine.width < nameW) { // spread letters to the name's width
        const extra = Math.min((nameW - tagLine.width) / (tagLine.count - 1), tagSize * 0.7);
        tagLine = layoutLine(tagFont, tagText, tagSize, tracking + extra);
      }
      parts.missing.push(...tagLine.missing);
    }
    const tagCap = tagLine ? tagLine.cap : 0;
    const textW = Math.max(nameW, tagLine ? tagLine.width : 0);
    const decorGap = cfg.decor === "rule" && tagLine ? N * 0.5 : 0;
    const gapNameTag = N * 0.34 + decorGap;

    const centered = layout === "stacked" || layout === "wordmark" || layout === "iconOnly";
    const mkSize = hasMark || layout === "iconOnly" ? (layout === "stacked" ? N * 2.5 : layout === "iconOnly" ? N * 3 : N * 1.95) * cfg.iconScale : 0;

    // ---- text block, origin at its top-left (width textW)
    const blockInner = [];
    let y = 0;
    const xFor = (w) => (centered ? (textW - w) / 2 : 0);
    laid.forEach((line, i) => {
      blockInner.push(lineMarkup(line, xFor(line.width), y + cap, nameFill, line.width));
      y += cap + (i < laid.length - 1 ? lineGap : 0);
    });
    let textBottom = y + Math.max(0, laid[laid.length - 1].bottom); // descenders hang below the last line
    if (tagLine) {
      y += Math.max(0, laid[laid.length - 1].bottom) + gapNameTag;
      if (cfg.decor === "rule") {
        const w = Math.min(textW, nameW) * (centered ? 0.34 : 0.28);
        blockInner.push(`<rect x="${r2(centered ? (textW - w) / 2 : 0)}" y="${r2(y - gapNameTag / 2 - decorGap / 2 - 1)}" width="${r2(w)}" height="2.4" rx="1.2" fill="${pal.rule}"/>`);
      }
      blockInner.push(lineMarkup(tagLine, xFor(tagLine.width), y + tagCap, pal.tagline, tagLine.width));
      if (cfg.decor === "flank") {
        const gap = tagSize * 0.7;
        const lw = Math.max(0, (textW - tagLine.width) / 2 - gap);
        if (lw > 6) {
          const yy = y + tagCap / 2;
          blockInner.push(`<rect x="0" y="${r2(yy - 1)}" width="${r2(lw)}" height="2" fill="${pal.rule}"/><rect x="${r2(textW - lw)}" y="${r2(yy - 1)}" width="${r2(lw)}" height="2" fill="${pal.rule}"/>`);
        }
      }
      textBottom = y + tagCap;
    } else if (cfg.decor === "bar") {
      /* handled below */
    }
    if (cfg.decor === "bar") {
      const bw = Math.max(26, nameW * 0.16);
      const by = textBottom + N * 0.2;
      blockInner.push(`<rect x="${r2(centered ? (textW - bw) / 2 : 0)}" y="${r2(by)}" width="${r2(bw)}" height="${r2(N * 0.09)}" rx="${r2(N * 0.045)}" fill="${pal.rule}"/>`);
      textBottom = by + N * 0.09;
    }
    const textH = textBottom;
    if (gradientText) {
      parts.defs.push(`<linearGradient id="${uid}-gt" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${r2(textW)}" y2="0"><stop offset="0" stop-color="${pal.fill}"/><stop offset="1" stop-color="${pal.fill2}"/></linearGradient>`);
    }

    const mark = mkSize ? markMarkup(cfg, pal, mkSize, uid, parts) : "";
    const place = (inner, x, yy) => `<g transform="translate(${r2(x)} ${r2(yy)})">${inner}</g>`;
    if (layout === "iconOnly") {
      box = { w: mkSize, h: mkSize, body: place(mark, 0, 0) };
    } else if (layout === "wordmark" || !mkSize) {
      box = { w: textW, h: textH, body: place(`<g>${blockInner.join("")}</g>`, 0, 0) };
    } else if (layout === "stacked") {
      const gap = N * 0.55;
      const w = Math.max(mkSize, textW);
      box = { w, h: mkSize + gap + textH, body: place(mark, (w - mkSize) / 2, 0) + place(blockInner.join(""), (w - textW) / 2, mkSize + gap) };
    } else { // horizontal, horizontalRight
      const gap = N * 0.55;
      const h = Math.max(mkSize, textH);
      const left = layout === "horizontal";
      const w = mkSize + gap + textW;
      box = {
        w, h,
        body: place(mark, left ? 0 : textW + gap, (h - mkSize) / 2) + place(blockInner.join(""), left ? mkSize + gap : 0, (h - textH) / 2),
      };
    }
  }

  // ---- finishing
  const defs = parts.defs.join("");
  let content = box.body;
  if (cfg.effect === "shadow" && !pal.mono) {
    parts.defs.push(`<filter id="${uid}-sh" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="${r2(box.h * 0.025)}" stdDeviation="${r2(box.h * 0.03)}" flood-color="#0b0f1a" flood-opacity=".32"/></filter>`);
    content = `<g filter="url(#${uid}-sh)">${content}</g>`;
  }
  const margin = Math.max(box.w, box.h) * pad;
  const width = r2(box.w + margin * 2);
  const height = r2(box.h + margin * 2);
  const bgRect = background ? `<rect width="${width}" height="${height}" fill="${background}"/>` : "";
  const allDefs = parts.defs.join("") || defs;
  const inner = `${bgRect}<g transform="translate(${r2(margin)} ${r2(margin)})">${content}</g>`;
  const svg = `<svg xmlns="${SVG_NS}" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="${escapeXml(cfg.name)} logo">${allDefs ? `<defs>${allDefs}</defs>` : ""}${inner}</svg>`;
  return { svg, width, height, missing: [...new Set(parts.missing)], palette: pal, inner, defs: allDefs };
}

export const escapeXml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" }[c]));

// ------------------------------------------------------------------------------------------ the round emblem
function emblem(cfg, pal, uid, parts, name, tagText, nameFill) {
  const R = 200;
  const D = R * 2;
  const container = CONTAINERS[cfg.container === "none" ? "circle" : cfg.container] || CONTAINERS.circle;
  const outline = cfg.containerStyle === "outline";
  const gradId = `${uid}-em`;
  let fill = pal.fill;
  if (cfg.containerStyle === "gradient" && !pal.mono) {
    parts.defs.push(`<linearGradient id="${gradId}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${pal.fill}"/><stop offset="1" stop-color="${pal.fill2}"/></linearGradient>`);
    fill = `url(#${gradId})`;
  }
  const ink = outline ? pal.fill : readableOn(pal.mono ? pal.fill : pal.fill);
  const ringInk = outline ? pal.fill : ink;
  let body = `<g transform="scale(${D / 100})"><path d="${container.d}" fill="${outline ? "none" : fill}" ${outline ? `stroke="${pal.fill}" stroke-width="3"` : ""} stroke-linejoin="round"/></g>`;
  const cx = R;
  const cy = R;
  const ring = (r, w, op = 1) => `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${ringInk}" stroke-width="${w}" stroke-opacity="${op}"/>`;
  body += ring(R * 0.9, 3.5, 0.9) + ring(R * 0.56, 2.5, 0.8);
  const textInk = outline ? pal.fill : (pal.mono ? pal.glyph : ink);
  const band = R * 0.9 - R * 0.56;
  // name on the upper arc
  let size = band * 0.62;
  const upperName = name;
  let line = layoutLine(cfg.font, upperName, size, trackPx(size, Math.min(cfg.tracking, 140)));
  const rc = (R * 0.9 + R * 0.56) / 2;
  const maxArc = Math.PI * (tagText ? 1.05 : 1.55);
  const rTop = rc - line.cap / 2;
  if (line.width / rTop > maxArc) {
    size *= (maxArc * rTop) / line.width;
    line = layoutLine(cfg.font, upperName, size, trackPx(size, Math.min(cfg.tracking, 140)));
  }
  parts.missing.push(...line.missing);
  const rTop2 = rc - line.cap / 2;
  const glyphsOn = (ln, radius, top) => ln.glyphs.filter((g) => g.d).map((g) => {
    const mid = g.x + g.adv / 2 - ln.width / 2 - ln.left * 0;
    const deg = (mid / radius) * (180 / Math.PI);
    const a = top ? -90 + deg : 90 - deg;
    const rot = top ? a + 90 : a - 90;
    const px = cx + radius * Math.cos((a * Math.PI) / 180);
    const py = cy + radius * Math.sin((a * Math.PI) / 180);
    return `<path d="${g.d}" transform="translate(${r2(px)} ${r2(py)}) rotate(${r2(rot)}) translate(${r2(-g.adv / 2 + 0)} 0)"/>`;
  }).join("");
  let fillName = Array.isArray(nameFill) ? ink : (String(nameFill).startsWith("url") ? ink : textInk);
  if (pal.mono) fillName = pal.glyph;
  body += `<g fill="${fillName}">${glyphsOn(line, rTop2, true)}</g>`;
  if (tagText) {
    const tsize = Math.min(band * 0.34, size * 0.62);
    let tl = layoutLine(cfg.textFont, tagText, tsize, trackPx(tsize, 220));
    const rBot = rc + tl.cap / 2;
    if (tl.width / rBot > Math.PI * 0.9) {
      const k = (Math.PI * 0.9 * rBot) / tl.width;
      tl = layoutLine(cfg.textFont, tagText, tsize * k, trackPx(tsize * k, 220));
    }
    parts.missing.push(...tl.missing);
    body += `<g fill="${pal.mono ? pal.glyph : textInk}" fill-opacity=".92">${glyphsOn(tl, rc + tl.cap / 2, false)}</g>`;
    const starPt = (deg) => [cx + rc * Math.cos((deg * Math.PI) / 180), cy + rc * Math.sin((deg * Math.PI) / 180)];
    [180, 0].forEach((deg) => {
      const [sx, sy] = starPt(deg);
      body += `<path d="${starShape(sx, sy, 9, 4)}" fill="${pal.mono ? pal.glyph : (outline ? pal.fill2 : (pal.glyphAccent || ink))}"/>`;
    });
  }
  // centre symbol
  const innerR = R * 0.5;
  const mk = markMarkup({ ...cfg, container: "none" }, { ...pal, iconMain: outline ? pal.fill : ink, iconAccent: pal.glyphAccent || ink, mono: pal.mono, fill: pal.fill, fill2: pal.fill2, glyph: ink }, innerR * 1.5, uid, parts);
  body += `<g transform="translate(${r2(cx - innerR * 0.75)} ${r2(cy - innerR * 0.75)})">${mk}</g>`;
  return { w: D, h: D, body };
}

function starShape(cx, cy, ro, ri) {
  const pts = [];
  for (let i = 0; i < 10; i += 1) {
    const a = (-90 + 36 * i) * (Math.PI / 180);
    const r = i % 2 ? ri : ro;
    pts.push(`${r2(cx + r * Math.cos(a))} ${r2(cy + r * Math.sin(a))}`);
  }
  return `M${pts.join("L")}z`;
}

// ------------------------------------------------------------------------------------------ helpers for sheets and mockups
// Text as outlines, drawn with its top at y = 0: { markup, width, height }.
export function outlineText(fontKey, text, size, { tracking = 0, fill = "#000000" } = {}) {
  const line = layoutLine(fontKey, text, size, trackPx(size, tracking));
  return { markup: lineMarkup(line, 0, line.cap, fill, line.width), width: line.width, height: line.cap + Math.max(0, line.bottom), cap: line.cap };
}

// A built logo fitted into a box (centred, never enlarged beyond `maxScale`). Returns markup for use inside another SVG.
export function placeLogo(logo, x, y, boxW, boxH, { maxScale = 99, align = "center" } = {}) {
  const k = Math.min(boxW / logo.width, boxH / logo.height, maxScale);
  const w = logo.width * k;
  const h = logo.height * k;
  const px = align === "left" ? x : x + (boxW - w) / 2;
  const py = y + (boxH - h) / 2;
  return `<svg x="${r2(px)}" y="${r2(py)}" width="${r2(w)}" height="${r2(h)}" viewBox="0 0 ${logo.width} ${logo.height}" overflow="visible">${logo.defs ? `<defs>${logo.defs}</defs>` : ""}${logo.inner}</svg>`;
}
